'use client';

import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LockTier } from '@/lib/search-gating';
import { cn } from '@/lib/utils';

export type { LockTier } from '@/lib/search-gating';

/**
 * The chip that says a filter is out of reach and that signing in unlocks it.
 *
 * Deliberately coloured: the chip was originally `bg-muted
 * text-muted-foreground`, which is the same grey the sidebar uses for
 * placeholders and disabled fields — so on a phone it read as "this control is
 * broken" rather than "there is something to unlock here".
 */
export function FilterLockBadge({
  className,
  iconOnly = false,
}: {
  lock?: LockTier;
  className?: string;
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <span
      aria-hidden={iconOnly || undefined}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full ring-1 ring-inset',
        iconOnly
          ? 'size-3.5 justify-center'
          : 'gap-0.5 px-1.5 py-px text-[10px] font-medium leading-none',
        'bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:ring-sky-800',
        className,
      )}
    >
      <Lock size={9} />
      {!iconOnly && t('search.sidebar.loginBadge', { defaultValue: 'Sign in' })}
    </span>
  );
}

/**
 * The tooltip that goes with a lock chip.
 *
 * `scope: 'group'` phrases it in the plural — a locked group header stands
 * in for every filter behind it, not for one.
 */
export function useLockHint(): (
  lock: LockTier,
  scope?: 'filter' | 'group',
) => string {
  const { t } = useTranslation();
  return (_lock, scope = 'filter') =>
    scope === 'group'
      ? t('search.sidebar.groupLoginHint', {
          defaultValue: 'Sign in for free to use these filters',
        })
      : t('search.sidebar.loginOnlyHint', {
          defaultValue: 'Sign in for free to use this filter',
        });
}
